import { useCallback, useState } from "react";

// Typed useState that persists to localStorage. Reads once on mount (lazy
// initializer); writes synchronously on each setValue call. Failures are
// silently ignored so private-browsing / quota errors never crash the UI.
export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item !== null ? (JSON.parse(item) as T) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T) => {
      setStoredValue(value);
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Best-effort: ignore write failures (private browsing, storage quota).
      }
    },
    [key]
  );

  return [storedValue, setValue];
}
